import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { BadgeCheck, Printer, ShieldCheck, XCircle } from "lucide-react";
import { API, adminApi } from "../lib/api";
import { CONTACT } from "../constants/contact";

const Line = ({ label, value, testId }) => (
  <p className="text-[11px] leading-tight"><span className="uppercase tracking-wider text-slate-500">{label}</span><br />
    <span data-testid={testId} className="text-sm font-semibold text-slate-900">{value}</span></p>
);

export default function IdCardPage() {
  const { token } = useParams();
  const [card, setCard] = useState(undefined);
  useEffect(() => {
    adminApi.get(`/id-cards/${token}`).then((r) => setCard(r.data)).catch(() => setCard(null));
  }, [token]);
  const url = `${window.location.origin}/id/${token}`;

  return (
    <div className="min-h-screen bg-[#0B1120] px-4 py-10 font-body text-slate-100 print:bg-white print:p-0">
      {card === null && <p data-testid="id-card-not-found" className="text-center text-red-300">This ID card was not found.</p>}
      {card && (
        <div className="mx-auto max-w-md space-y-6">
          <p data-testid="id-card-status" className={`flex items-center justify-center gap-2 py-2 text-sm font-semibold print:hidden ${card.valid ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
            {card.valid ? <><BadgeCheck className="h-4 w-4" />Verified employee: KYC approved by Balangir Group</> : <><XCircle className="h-4 w-4" />Not valid: KYC is not approved</>}
          </p>
          <article data-testid="id-card" className="mx-auto w-[340px] overflow-hidden bg-white text-slate-900 shadow-2xl print:shadow-none" style={{ printColorAdjust: "exact", WebkitPrintColorAdjust: "exact" }}>
            <header className="flex items-center gap-2 bg-orange-500 px-4 py-3 text-white">
              <ShieldCheck className="h-6 w-6" />
              <div className="leading-tight"><p className="font-heading text-lg font-extrabold">Balangir Group</p>
                <p className="text-[10px] uppercase tracking-[0.2em]">Employee Identity Card</p></div>
            </header>
            <div className="flex gap-4 p-4">
              <img src={`${API}/id-cards/${token}/photo`} alt="Employee" data-testid="id-card-photo" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="h-32 w-24 shrink-0 border border-slate-300 bg-slate-100 object-cover" />
              <div className="space-y-2">
                <p data-testid="id-card-name" className="font-heading text-lg font-extrabold leading-tight">{card.full_name}</p>
                <Line label="ID / Reference" value={card.reference} testId="id-card-reference" />
                <Line label="Role" value={card.job_role} testId="id-card-role" />
                <Line label="District" value={`${card.district}, ${card.state}`} testId="id-card-district" />
              </div>
            </div>
            <div className="flex items-end justify-between border-t border-slate-200 px-4 py-3">
              <p className="max-w-[180px] text-[10px] leading-snug text-slate-500">Scan to verify this card. If found, please return to Balangir Group, {CONTACT.phone}.</p>
              <QRCodeSVG value={url} size={84} data-testid="id-card-qr" />
            </div>
            <p className={`py-1 text-center text-[10px] font-semibold uppercase tracking-widest ${card.valid ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>{card.valid ? "KYC Verified" : "Not Valid"}</p>
          </article>
          <button onClick={() => window.print()} data-testid="id-card-print-button"
            className="mx-auto flex items-center gap-2 bg-orange-500 px-6 py-3 font-semibold text-white hover:bg-orange-600 print:hidden">
            <Printer className="h-4 w-4" />Print / Save as PDF
          </button>
        </div>
      )}
    </div>
  );
}
